import { useState, useMemo } from 'react';
import { Button, Input, Icon, SlideOutPanel, SlideOutPanelBody, DatePicker } from '@repo/ui';
import type { TrustEligibleMatterDto } from '../types';
import { formatCurrency, formatDate, DateFormat } from '@xtnd/web/shared/utils/format';
import { calculateTrustAmount } from '../utils/trustCalculation';

export interface MatterSelectionTableProps {
  matters: TrustEligibleMatterDto[];
  selectedMatters: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  editableAmounts?: Record<string, number>;
  onAmountChange?: (matterId: string, amount: number) => void;
  /** Overrides for WIP/Outstanding/Trust used to recalc Calc Amount and TR Amount */
  editableWip?: Record<string, number>;
  editableOutstanding?: Record<string, number>;
  editableTrustBalance?: Record<string, number>;
  onWipChange?: (matterId: string, value: number) => void;
  onOutstandingChange?: (matterId: string, value: number) => void;
  onTrustBalanceChange?: (matterId: string, value: number) => void;
  editableDates?: Record<string, { issueDate?: Date; dueDate?: Date }>;
  onDateChange?: (matterId: string, field: 'issueDate' | 'dueDate', date: Date | undefined) => void;
  editableEmailTypes?: Record<string, 'Trust Establishment' | 'Mid-Cycle Request' | 'Final Bill'>;
  onEmailTypeChange?: (matterId: string, emailType: 'Trust Establishment' | 'Mid-Cycle Request' | 'Final Bill') => void;
  defaultEmailType?: 'Trust Establishment' | 'Mid-Cycle Request' | 'Final Bill';
  defaultIssueDate?: Date;
  defaultDueDate?: Date;
  visibleColumns?: Record<string, boolean>;
  totalRecords?: number; // Total records across all pages
  isSelectAllPages?: boolean; // Whether all pages are selected
  onSelectAllPages?: (selected: boolean) => void; // Callback for selecting all pages
  onRefresh?: () => void; // Callback for refreshing data
  isRefreshing?: boolean; // Whether data is currently being refreshed
  replication?: {
    matters: {
      lastReplication: string | null;
      committedWatermark?: string | null;
      updatedAt: string;
    } | null;
    bills: {
      lastReplication: string | null;
      committedWatermark?: string | null;
      updatedAt: string;
    } | null;
    activities: {
      lastReplication: string | null;
      committedWatermark?: string | null;
      updatedAt: string;
    } | null;
    tenantLock: {
      exists: boolean;
      executionOwner?: string;
      leaseExpiresAtEpoch?: number;
    };
  };
}

type SortField = keyof TrustEligibleMatterDto;
type SortDirection = 'asc' | 'desc' | null;

export function MatterSelectionTable({
  matters,
  selectedMatters,
  onSelectionChange,
  editableAmounts = {},
  onAmountChange,
  editableWip = {},
  editableOutstanding = {},
  editableTrustBalance = {},
  onWipChange,
  onOutstandingChange,
  onTrustBalanceChange,
  editableDates = {},
  onDateChange,
  editableEmailTypes = {},
  onEmailTypeChange,
  defaultEmailType,
  defaultIssueDate,
  defaultDueDate,
  visibleColumns = {
    matterDisplayNumber: true,
    clientName: true,
    matterStatus: true,
    minimumThreshold: true,
    trustBalance: true,
    outstandingBalance: true,
    wip: true,
    calculatedTrustAmount: true,
    trustRequestAmount: true,
    trustRequestEmailType: true,
    issueDate: true,
    dueDate: true,
    lastPaymentDate: true,
    lastInvoiceDate: true,
  },
  totalRecords,
  isSelectAllPages = false,
  onSelectAllPages,
  onRefresh,
  isRefreshing = false,
  replication,
}: MatterSelectionTableProps) {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedMatterDetails, setSelectedMatterDetails] = useState<TrustEligibleMatterDto | null>(null);

  const handleSelectAll = (checked: boolean) => {
    if (isSelectAllPages && onSelectAllPages) {
      // If currently in "select all pages" mode, toggle it off
      onSelectAllPages(false);
    } else if (checked) {
      // Select all on current page
      const currentPageIds = matters.map(m => m.id);
      const newSelected = [...new Set([...selectedMatters, ...currentPageIds])];
      onSelectionChange(newSelected);
    } else {
      // Deselect all on current page
      const currentPageIds = new Set(matters.map(m => m.id));
      const newSelected = selectedMatters.filter(id => !currentPageIds.has(id));
      onSelectionChange(newSelected);
    }
  };

  const handleSelectMatter = (matterId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedMatters, matterId]);
    } else {
      onSelectionChange(selectedMatters.filter(id => id !== matterId));
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAmountChange = (matterId: string, amount: number) => {
    if (onAmountChange) {
      onAmountChange(matterId, amount);
    }
  };

  const handleShowDetails = (matter: TrustEligibleMatterDto) => {
    setSelectedMatterDetails(matter);
    setDetailsOpen(true);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <Icon name="HiArrowsUpDown" size="sm" className="ml-1" />;
    }
    if (sortDirection === 'asc') {
      return <Icon name="HiArrowUp" size="sm" className="ml-1" />;
    }
    if (sortDirection === 'desc') {
      return <Icon name="HiArrowDown" size="sm" className="ml-1" />;
    }
    return <Icon name="HiArrowsUpDown" size="sm" className="ml-1" />;
  };

  const sortedMatters = useMemo(() => {
    if (!sortField || !sortDirection) return matters;

    return [...matters].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [matters, sortField, sortDirection]);

  // Helper to prefer lastReplication -> committedWatermark -> updatedAt
  const formatReplicationTimestamp = (item?: {
    lastReplication?: string | null;
    committedWatermark?: string | null;
    updatedAt?: string | null;
  } | null): string | null => {
    if (!item) return null;
    return (item.lastReplication || item.committedWatermark || item.updatedAt) ?? null;
  };

  const currentPageSelected = matters.filter(m => selectedMatters.includes(m.id));
  const allCurrentPageSelected = matters.length > 0 && currentPageSelected.length === matters.length;
  const someCurrentPageSelected = currentPageSelected.length > 0 && currentPageSelected.length < matters.length;

  // For the checkbox state: if select all pages is active, show as checked
  // Otherwise, show based on current page selection
  const checkboxChecked = isSelectAllPages || allCurrentPageSelected;
  const checkboxIndeterminate = !isSelectAllPages && someCurrentPageSelected;

  return (
    <div className="overflow-x-auto max-w-full">
      <table className="min-w-full divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600 relative table-fixed">
        <thead className="bg-white dark:bg-xtnd-dark-700 sticky top-0 z-30">
          <tr>
            <th className="w-12 px-4 py-3 text-left">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={checkboxChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = checkboxIndeterminate;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 text-xtnd-blue bg-xtnd-dark-50 border-xtnd-dark-200 rounded focus:ring-xtnd-blue focus:ring-2"
                  title={isSelectAllPages ? `All ${totalRecords || 0} matters selected across all pages` : 'Select all matters on this page'}
                />
                {onRefresh && (
                  <button
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    className="p-1 text-xtnd-dark-300 hover:text-xtnd-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded"
                    title="Refresh matters data"
                  >
                    <Icon
                      name="HiRefresh"
                      className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                    />
                  </button>
                )}
                {isSelectAllPages && (
                  <span className="text-xs text-xtnd-blue font-medium">
                    All pages
                  </span>
                )}
                {!isSelectAllPages && allCurrentPageSelected && totalRecords && totalRecords > matters.length && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSelectAllPages?.(true)}
                    className="text-xs text-xtnd-blue hover:text-xtnd-blue-700"
                  >
                    Select all {totalRecords} matters
                  </Button>
                )}
              </div>
            </th>
            <th className="w-32 px-4 py-3 text-left">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('matterDisplayNumber')}
                className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
              >
                Matter
                {getSortIcon('matterDisplayNumber')}
              </Button>
            </th>
            <th className="w-48 px-4 py-3 text-left">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('clientName')}
                className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
              >
                Client
                {getSortIcon('clientName')}
              </Button>
            </th>
            <th className="w-20 px-4 py-3 text-left">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('matterStatus')}
                className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
              >
                Status
                {getSortIcon('matterStatus')}
              </Button>
            </th>
            {visibleColumns.minimumThreshold && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('minimumThreshold')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Min Threshold
                  {getSortIcon('minimumThreshold')}
                </Button>
              </th>
            )}
            {visibleColumns.trustBalance && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('trustBalance')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Trust Balance
                  {getSortIcon('trustBalance')}
                </Button>
              </th>
            )}
            {visibleColumns.outstandingBalance && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('outstandingBalance')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Outstanding
                  {getSortIcon('outstandingBalance')}
                </Button>
              </th>
            )}
            {visibleColumns.wip && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('wip')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  WIP
                  {getSortIcon('wip')}
                </Button>
              </th>
            )}
            {visibleColumns.calculatedTrustAmount && (
              <th className="w-28 px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('calculatedTrustAmount')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Calc Amount
                  {getSortIcon('calculatedTrustAmount')}
                </Button>
              </th>
            )}
            <th className="w-32 px-4 py-3 text-left">
              <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">TR Amount</span>
            </th>
            {visibleColumns.trustRequestEmailType && (
              <th className="w-48 min-w-[155px] px-4 py-3 text-left">
                <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">Email Type</span>
              </th>
            )}
            {visibleColumns.issueDate && (
              <th className="w-40 px-4 py-3 text-left">
                <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">Issue Date</span>
              </th>
            )}
            {visibleColumns.dueDate && (
              <th className="w-40 px-4 py-3 text-left">
                <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">Due Date</span>
              </th>
            )}
            {visibleColumns.lastPaymentDate && (
              <th className="w-32 px-4 py-3 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('lastPaymentDate')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Last Payment
                  {getSortIcon('lastPaymentDate')}
                </Button>
              </th>
            )}
            {visibleColumns.lastInvoiceDate && (
              <th className="w-32 px-4 py-3 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('lastInvoiceDate')}
                  className="h-auto p-0 font-semibold text-xtnd-dark dark:text-xtnd-light hover:text-xtnd-blue"
                >
                  Last Invoice
                  {getSortIcon('lastInvoiceDate')}
                </Button>
              </th>
            )}
            <th className="w-12 px-4 py-3 text-center">
              {/* Info button column */}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-xtnd-dark-700 divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
          {sortedMatters.map((matter) => {
            const isSelected = selectedMatters.includes(matter.id);
            const effectiveWip = editableWip[matter.id] ?? matter.wip;
            const effectiveOutstanding = editableOutstanding[matter.id] ?? matter.outstandingBalance;
            const effectiveTrust = editableTrustBalance[matter.id] ?? matter.trustBalance;
            const calculatedAmount = calculateTrustAmount(
              matter.minimumThreshold,
              effectiveTrust,
              effectiveOutstanding,
              effectiveWip
            );
            const displayAmount = editableAmounts[matter.id] ?? calculatedAmount;
            const inputValue = displayAmount !== undefined ? displayAmount.toFixed(2) : '';

            return (
              <tr
                key={matter.id}
                className={`hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600 ${
                  isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleSelectMatter(matter.id, e.target.checked)}
                    className="w-4 h-4 text-xtnd-blue bg-xtnd-dark-50 border-xtnd-dark-200 rounded focus:ring-xtnd-blue focus:ring-2"
                  />
                </td>
                <td className="px-4 py-3 font-medium text-xtnd-blue dark:text-xtnd-blue-300">
                  <a
                    href={`https://app.clio.com/nc/#/matters/${matter.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    title="View matter in Clio"
                  >
                    {matter.matterDisplayNumber}
                  </a>
                </td>
                <td className="px-4 py-3 text-xtnd-dark dark:text-xtnd-light">
                  <a
                    href={`https://app.clio.com/nc/#/contacts/${matter.clientId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    title="View contact in Clio"
                  >
                    {matter.clientName}
                  </a>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      matter.matterStatus === 'Open'
                        ? 'bg-xtnd-light text-xtnd-dark dark:bg-xtnd-blue-900 dark:text-xtnd-blue-200'
                        : 'bg-xtnd-dark-700 text-xtnd-white dark:bg-xtnd-dark-600 dark:text-xtnd-light'
                    }`}
                  >
                    {matter.matterStatus}
                  </span>
                </td>
                {visibleColumns.minimumThreshold && (
                  <td className="px-4 py-3 font-mono text-right text-xtnd-dark dark:text-xtnd-light">
                    {formatCurrency(matter.minimumThreshold)}
                  </td>
                )}
                {visibleColumns.trustBalance && (
                  <td className="px-4 py-3 text-right">
                    <Input
                      type="number"
                      value={effectiveTrust}
                      onChange={(e) => onTrustBalanceChange?.(matter.id, Number(e.target.value) || 0)}
                      className="w-full min-w-[5rem] text-right font-mono"
                      min="0"
                      step="0.01"
                    />
                  </td>
                )}
                {visibleColumns.outstandingBalance && (
                  <td className="px-4 py-3 text-right">
                    <Input
                      type="number"
                      value={effectiveOutstanding}
                      onChange={(e) => onOutstandingChange?.(matter.id, Number(e.target.value) || 0)}
                      className="w-full min-w-[5rem] text-right font-mono"
                      min="0"
                      step="0.01"
                    />
                  </td>
                )}
                {visibleColumns.wip && (
                  <td className="px-4 py-3 text-right">
                    <Input
                      type="number"
                      value={effectiveWip}
                      onChange={(e) => onWipChange?.(matter.id, Number(e.target.value) || 0)}
                      className="w-full min-w-[5rem] text-right font-mono"
                      min="0"
                      step="0.01"
                    />
                  </td>
                )}
                {visibleColumns.calculatedTrustAmount && (
                  <td className="px-4 py-3 font-mono text-right font-semibold text-xtnd-blue dark:text-xtnd-blue-300">
                    {formatCurrency(calculatedAmount)}
                  </td>
                )}
                <td className="px-4 py-3">
                  <Input
                    type="number"
                    value={inputValue}
                    onChange={(e) => handleAmountChange(matter.id, Number(e.target.value))}
                    className="w-32 text-right font-mono"
                    min="0"
                    step="0.01"
                  />
                </td>
                {visibleColumns.trustRequestEmailType && (
                  <td className="px-4 py-3">
                    <select
                      value={editableEmailTypes[matter.id] || defaultEmailType || ''}
                      onChange={(e) => onEmailTypeChange?.(matter.id, e.target.value as 'Trust Establishment' | 'Mid-Cycle Request' | 'Final Bill')}
                      className="w-full px-2 py-1 text-sm border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded focus:outline-none focus:ring-1 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
                    >
                      <option value="">Select...</option>
                      <option value="Trust Establishment">Trust Establishment</option>
                      <option value="Mid-Cycle Request">Mid-Cycle Request</option>
                      <option value="Final Bill">Final Bill</option>
                    </select>
                  </td>
                )}
                {visibleColumns.issueDate && (
                  <td className="px-4 py-3">
                    <div
                      className="date-picker-container"
                      style={{
                        width: '100%',
                        minWidth: '160px',
                        maxWidth: '100%',
                        position: 'relative'
                      }}
                    >
                      <DatePicker
                        mode="single"
                        value={editableDates[matter.id]?.issueDate || defaultIssueDate}
                        onChange={(date) => onDateChange?.(matter.id, 'issueDate', date || undefined)}
                        className="date-picker-compact"
                      />
                      <style dangerouslySetInnerHTML={{
                        __html: `
                          .date-picker-container .date-picker-compact button span {
                            min-width: auto !important;
                            max-width: 140px !important;
                            overflow: hidden !important;
                            text-overflow: ellipsis !important;
                            white-space: nowrap !important;
                          }
                          .date-picker-container .date-picker-compact button {
                            width: 100% !important;
                            max-width: 100% !important;
                          }
                        `
                      }} />
                    </div>
                  </td>
                )}
                {visibleColumns.dueDate && (
                  <td className="px-4 py-3">
                    <div
                      className="date-picker-container"
                      style={{
                        width: '100%',
                        minWidth: '160px',
                        maxWidth: '100%',
                        position: 'relative'
                      }}
                    >
                      <DatePicker
                        mode="single"
                        value={editableDates[matter.id]?.dueDate || defaultDueDate}
                        onChange={(date) => onDateChange?.(matter.id, 'dueDate', date || undefined)}
                        className="date-picker-compact"
                      />
                      <style dangerouslySetInnerHTML={{
                        __html: `
                          .date-picker-container .date-picker-compact button span {
                            min-width: auto !important;
                            max-width: 140px !important;
                            overflow: hidden !important;
                            text-overflow: ellipsis !important;
                            white-space: nowrap !important;
                          }
                          .date-picker-container .date-picker-compact button {
                            width: 100% !important;
                            max-width: 100% !important;
                          }
                        `
                      }} />
                    </div>
                  </td>
                )}
                {visibleColumns.lastPaymentDate && (
                  <td className="px-4 py-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                    {matter.lastPaymentDate
                      ? formatDate(matter.lastPaymentDate, DateFormat.US)
                      : 'No payments'}
                  </td>
                )}
                {visibleColumns.lastInvoiceDate && (
                  <td className="px-4 py-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                    {matter.lastInvoiceDate
                      ? formatDate(matter.lastInvoiceDate, DateFormat.US)
                      : 'No invoices'}
                  </td>
                )}
                <td className="px-4 py-3 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleShowDetails(matter)}
                    className="h-10 w-10 p-0"
                    title="View matter details"
                  >
                    <Icon name="InfoIcon" size="sm" />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {matters.length === 0 && (
        <div className="p-8 text-center text-xtnd-dark-600 dark:text-xtnd-light-400">
          <Icon name="InfoIcon" size="xl" className="mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2 text-xtnd-dark dark:text-xtnd-light">No matters found</h3>
          <p>Try adjusting your filters to see more results.</p>
        </div>
      )}

      {/* Matter Details Slide-out Panel */}
      <SlideOutPanel
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title="Matter Details"
        width="md"
      >
        <SlideOutPanelBody>
          {selectedMatterDetails && (
            <div className="space-y-6">
              {/* Matter Information */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Matter Number</p>
                  <p className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                    <a
                      href={`https://app.clio.com/nc/#/matters/${selectedMatterDetails.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                      title="View matter in Clio"
                    >
                      {selectedMatterDetails.matterDisplayNumber}
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Client</p>
                  <p className="font-medium text-xtnd-dark dark:text-xtnd-light">
                    <a
                      href={`https://app.clio.com/nc/#/contacts/${selectedMatterDetails.clientId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                      title="View contact in Clio"
                    >
                      {selectedMatterDetails.clientName}
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Status</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedMatterDetails.matterStatus === 'Open'
                        ? 'bg-xtnd-light text-xtnd-dark dark:bg-xtnd-blue-900 dark:text-xtnd-blue-200'
                        : 'bg-xtnd-dark-700 text-xtnd-white dark:bg-xtnd-dark-600 dark:text-xtnd-light'
                    }`}
                  >
                    {selectedMatterDetails.matterStatus}
                  </span>
                </div>
              </div>

              {/* Trust Calculation Details - uses effective values (editable overrides) when present */}
              {(() => {
                const sid = selectedMatterDetails.id;
                const panelWip = editableWip[sid] ?? selectedMatterDetails.wip;
                const panelOutstanding = editableOutstanding[sid] ?? selectedMatterDetails.outstandingBalance;
                const panelTrust = editableTrustBalance[sid] ?? selectedMatterDetails.trustBalance;
                const panelCalculated = calculateTrustAmount(
                  selectedMatterDetails.minimumThreshold,
                  panelTrust,
                  panelOutstanding,
                  panelWip
                );
                return (
                  <div className="rounded-lg bg-xtnd-light dark:bg-xtnd-dark-700 p-4 space-y-3">
                    <h4 className="font-semibold text-sm text-xtnd-dark dark:text-xtnd-light">Trust Calculation</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Minimum Threshold</span>
                        <span className="font-mono font-medium text-xtnd-dark dark:text-xtnd-light">
                          {formatCurrency(selectedMatterDetails.minimumThreshold)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Trust Balance</span>
                        <span className="font-mono font-medium text-xtnd-dark dark:text-xtnd-light">
                          {formatCurrency(panelTrust)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Outstanding Balance</span>
                        <span className="font-mono font-medium text-xtnd-dark dark:text-xtnd-light">
                          {formatCurrency(panelOutstanding)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">WIP Balance</span>
                        <span className="font-mono font-medium text-xtnd-dark dark:text-xtnd-light">
                          {formatCurrency(panelWip)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-xtnd-dark-200 dark:border-xtnd-dark-600">
                        <span className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Calculated Amount</span>
                        <span className="font-mono font-bold text-xtnd-blue dark:text-xtnd-blue-300">
                          {formatCurrency(panelCalculated)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Activity Dates */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Last Payment</span>
                  <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                    {selectedMatterDetails.lastPaymentDate
                      ? formatDate(selectedMatterDetails.lastPaymentDate, DateFormat.US_WITH_TIME)
                      : 'No payments'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Last Invoice</span>
                  <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                    {selectedMatterDetails.lastInvoiceDate
                      ? formatDate(selectedMatterDetails.lastInvoiceDate, DateFormat.US_WITH_TIME)
                      : 'No invoices'}
                  </span>
                </div>
              </div>

              {/* Data Update Timestamps */}
              {selectedMatterDetails.updatedAt && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-xtnd-dark dark:text-xtnd-light">Last Data Updates</h4>
                  <div className="space-y-3">
                    {selectedMatterDetails.updatedAt.matter && (
                      <div className="flex justify-between">
                        <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Matter</span>
                        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                          {formatDate(selectedMatterDetails.updatedAt.matter, DateFormat.US_WITH_TIME)}
                        </span>
                      </div>
                    )}

                    {/* Individual Bills */}
                    {selectedMatterDetails.updatedAt.bills && selectedMatterDetails.updatedAt.bills.allBills.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-light-400">Bills</span>
                          <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light-500">
                            ({selectedMatterDetails.updatedAt.bills.allBills.length} bill{selectedMatterDetails.updatedAt.bills.allBills.length !== 1 ? 's' : ''})
                          </span>
                        </div>
                        <div className="space-y-1 ml-4">
                          {selectedMatterDetails.updatedAt.bills.allBills.map((bill, index) => (
                            <div key={bill.id || index} className="flex justify-between items-center py-1 px-2 rounded bg-xtnd-light-50 dark:bg-xtnd-dark-700">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-xtnd-dark dark:text-xtnd-light">
                                    {bill.number || `Bill ${bill.id}`}
                                  </span>
                                  <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full ${
                                    bill.state === 'awaiting_payment'
                                      ? 'bg-xtnd-blue-100 text-xtnd-blue-800 dark:bg-xtnd-blue-900 dark:text-xtnd-blue-200'
                                      : bill.state === 'draft'
                                      ? 'bg-xtnd-dark-100 text-xtnd-dark-800 dark:bg-xtnd-dark-600 dark:text-xtnd-light-300'
                                      : bill.state === 'awaiting_approval'
                                      ? 'bg-xtnd-light-100 text-xtnd-dark-800 dark:bg-xtnd-light-900 dark:text-xtnd-light-200'
                                      : 'bg-xtnd-dark-100 text-xtnd-dark-600 dark:bg-xtnd-dark-500 dark:text-xtnd-light-400'
                                  }`}>
                                    {bill.state.replace('_', ' ')}
                                  </span>
                                  <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light-500">
                                    ({bill.kind.replace('_', ' ')})
                                  </span>
                                </div>
                                <div className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400 mt-0.5">
                                  Updated: {formatDate(bill.updatedAt, DateFormat.US_WITH_TIME)}
                                  {bill.due && bill.due > 0 && (
                                    <span className="ml-2 text-xtnd-blue-600 dark:text-xtnd-blue-400">
                                      Due: {formatCurrency(bill.due)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedMatterDetails.updatedAt.contact && (
                      <div className="flex justify-between">
                        <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Contact</span>
                        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                          {formatDate(selectedMatterDetails.updatedAt.contact, DateFormat.US_WITH_TIME)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Replication Status */}
              {replication && (
                <div className="space-y-4 pt-4 border-t border-xtnd-dark-200 dark:border-xtnd-dark-600">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-xtnd-dark dark:text-xtnd-light">Replication Status</h4>
                    {replication.tenantLock.exists && (
                      <div className="flex items-center gap-2">
                        <Icon name="HiRefresh" className="h-4 w-4 animate-spin text-xtnd-blue" />
                        <span className="text-xs font-medium text-xtnd-blue">In Progress</span>
                      </div>
                    )}
                  </div>
                  
                  {replication.tenantLock.exists && (
                    <div className="rounded-lg bg-xtnd-blue-50 dark:bg-xtnd-blue-900/20 p-3 mb-3">
                      <div className="flex items-start gap-2">
                        <Icon name="InfoIcon" className="h-4 w-4 text-xtnd-blue mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-xtnd-blue-800 dark:text-xtnd-blue-200">
                          <p className="font-medium mb-1">Replication running — data may be updating</p>

                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Matters</span>
                      <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                        {(() => {
                          const ts = formatReplicationTimestamp(replication.matters);
                          return ts ? `${formatDate(ts, DateFormat.US_WITH_TIME)} ET` : 'No replication recorded';
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Bills</span>
                      <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                        {(() => {
                          const ts = formatReplicationTimestamp(replication.bills);
                          return ts ? `${formatDate(ts, DateFormat.US_WITH_TIME)} ET` : 'No replication recorded';
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Activities</span>
                      <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                        {(() => {
                          const ts = formatReplicationTimestamp(replication.activities);
                          return ts ? `${formatDate(ts, DateFormat.US_WITH_TIME)} ET` : 'No replication recorded';
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </SlideOutPanelBody>
      </SlideOutPanel>
    </div>
  );
}

